import { css } from "@emotion/react";
import React, { useCallback, useRef, useState } from "react";
import { Popper, PopperBaseProps } from "../Popper";
import { useTheme } from "../theme";

const classNames = {
  tooltip: {
    arrow: "m2mui_Tooltip_arrow",
  },
};

// span（phrasing content）には p や div などのフロー要素を内包できず、
// inline 要素ではブロック子要素を正しく内包できないため Popper のアンカー矩形も
// 不安定になる。そのため、ブロックレベルの intrinsic 要素を子に持つ場合は
// div でラップする。
const BLOCK_LEVEL_ELEMENTS = new Set<string>([
  "address",
  "article",
  "aside",
  "blockquote",
  "dd",
  "details",
  "dialog",
  "div",
  "dl",
  "dt",
  "fieldset",
  "figcaption",
  "figure",
  "footer",
  "form",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "header",
  "hgroup",
  "hr",
  "li",
  "main",
  "nav",
  "ol",
  "p",
  "pre",
  "section",
  "table",
  "ul",
]);

export interface TooltipProps extends Pick<PopperBaseProps, "placement"> {
  /**
   * Tooltipに表示するテキスト
   */
  title: string;
  /**
   * Tooltipを表示する要素
   * 注意: disabledなボタン要素ではマウスイベントが発火しないため、Tooltipは機能しません
   */
  children: React.ReactElement | string;
  /**
   * 矢印を表示します。
   *
   * @default false
   */
  arrow?: boolean;
  /**
   * デフォルトの子要素にホバーした際に表示する機能を無視して、表示するかどうかを指定します。
   *
   * @default undefined
   */
  open?: boolean;
}

export const Tooltip = ({
  children,
  title,
  placement,
  arrow,
  open: openProp,
}: TooltipProps) => {
  const [node, setNode] = useState<HTMLElement | null>(null);

  const theme = useTheme();

  const isControlled = openProp !== undefined;

  const [mounted, setMounted] = useState(false);
  const prevOpen = useRef<boolean>(undefined);
  const [hidden, setHidden] = useState(true);

  if (isControlled && openProp !== prevOpen.current) {
    prevOpen.current = openProp;
    if (openProp) {
      setMounted(true);
      setHidden(false);
    } else {
      setHidden(true);
    }
  }

  const handleMouseEnter = useCallback(() => {
    if (!isControlled) {
      setMounted(true);
      setHidden(false);
    }
  }, [isControlled]);

  const handleMouseLeave = useCallback(() => {
    if (!isControlled) {
      setHidden(true);
    }
  }, [isControlled]);

  // cloneElement で子要素へ直接ハンドラを付与していた頃は、disabled な要素では
  // マウスイベントが発火せず Tooltip が表示されなかった。ラッパーへハンドラが
  // 付くようになっても、その挙動を維持するために disabled な子要素では
  // ホバーによる表示を行わない。
  const isDisabledChild =
    typeof children !== "string" &&
    (children.props as { disabled?: boolean }).disabled === true;

  const Wrapper =
    typeof children !== "string" &&
    typeof children.type === "string" &&
    BLOCK_LEVEL_ELEMENTS.has(children.type)
      ? "div"
      : "span";

  return (
    <React.Fragment>
      <Wrapper
        ref={setNode}
        onMouseEnter={isDisabledChild ? undefined : handleMouseEnter}
        onMouseLeave={isDisabledChild ? undefined : handleMouseLeave}
      >
        {children}
      </Wrapper>
      <Popper
        anchorEl={node}
        placement={placement}
        open={mounted}
        aria-hidden={hidden}
        disablePortal
        onTransitionEnd={() => {
          if (hidden) {
            setMounted(false);
          }
        }}
        css={css`
          opacity: 1;
          transition: opacity 200ms;
          &[aria-hidden="true"] {
            opacity: 0;
          }
          &[data-popper-placement*="bottom"] {
            .${classNames.tooltip.arrow} {
              top: 0;
              left: 12px;
              width: 12px;
              height: 6px;
              margin-top: -6px;
              &::before {
                border-color: transparent transparent currentcolor transparent;
                border-width: 0 6px 6px 6px;
              }
            }
            & > * {
              margin-top: 8px;
            }
          }
          &[data-popper-placement*="top"] {
            .${classNames.tooltip.arrow} {
              bottom: 0;
              left: 12px;
              width: 12px;
              height: 6px;
              margin-bottom: -6px;
              &::before {
                border-color: currentcolor transparent transparent transparent;
                border-width: 6px 6px 0 6px;
              }
            }
            & > * {
              margin-bottom: 8px;
            }
          }
          &[data-popper-placement*="right"] {
            .${classNames.tooltip.arrow} {
              top: 12px;
              left: 0;
              width: 6px;
              height: 12px;
              margin-left: -6px;
              &::before {
                border-color: transparent currentcolor transparent transparent;
                border-width: 6px 6px 6px 0;
              }
            }
            & > * {
              margin-left: 8px;
            }
          }
          &[data-popper-placement*="left"] {
            .${classNames.tooltip.arrow} {
              top: 12px;
              right: 0;
              width: 6px;
              height: 12px;
              margin-right: -6px;
              &::before {
                border-color: transparent transparent transparent currentcolor;
                border-width: 6px 0 6px 6px;
              }
            }
            & > * {
              margin-right: 8px;
            }
          }
        `}
      >
        <div
          css={[
            theme.typography.caption,
            css`
              position: relative;
              max-width: 300px;
              padding: 6px 10px;
              color: #fff;
              text-align: left;
              word-wrap: break-word;
              background-color: rgba(97, 97, 97, 0.9);
              border-radius: 4px;
            `,
          ]}
        >
          {title}
          {arrow ? (
            <div
              className={classNames.tooltip.arrow}
              css={css`
                position: absolute;
                color: rgba(97, 97, 97, 0.9);
                &::before {
                  display: block;
                  width: 0px;
                  height: 0px;
                  margin: auto;
                  content: "";
                  border-style: solid;
                }
              `}
            />
          ) : null}
        </div>
      </Popper>
    </React.Fragment>
  );
};
